// ConfigurationEdit.tsx
import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Typography,
  Row,
  Col,
  Tooltip,
  Radio,
  Checkbox,
  message,
  Upload,
  Space,
  ColorPicker,
  InputNumber,
} from "antd";
import { CopyOutlined, GlobalOutlined, UploadOutlined, UserOutlined } from "@ant-design/icons";
import {
  Button as ButtonDto,
  ButtonFunction,
  ButtonLocation,
  ConfigurationDto,
  EntraSecurityAdminDto,
  SecurityLevel,
  StyleDto,
} from "../../../services/ApiTypes.ts";
import { SecurityDisplayLevel } from "../../../services/Enums.ts";
import "./ConfigurationEdit.css";
import ButtonEditModal from "./ButtonEditModal.tsx";
import SortableButtonList from "./SortableButtonList.tsx";
import { ButtonItem } from "./ButtonItem.ts";
import { useNavigate, useParams } from "react-router-dom";
import { useGetConfigurationQuery, useUpdateConfigurationMutation } from "../../../services/ApiSlice.ts";
import CustomSpinner from "../../core/spinner/CustomSpinner.tsx";
import {useNavigationGuard} from "../../shared/guard/useNavigationGuard.ts"
import EntraSecurityModal from "../entraId/EntraModal.tsx";
import appConfig from "../../../config/appConfig.ts";

const { Title } = Typography;
const { Option } = Select;

const emptyGuid = "00000000-0000-0000-0000-000000000000"

const defaultStyle: StyleDto = {
  primaryColor: '1890ff',
  secondaryColor: 'ffffff',
  backgroundColor: 'f7f7f7',
  titleDisplay: 'flex',
  titleSize: '24',
  titleFont: 'Arial',
  subTitleSize: '20',
  subTitleFont: 'Arial',
  logoDisplay: 'flex',
  logoSize: '100',
  loginPageLogoSize: '150',
  logoOrder: '0',
};

export const defaultButton: ButtonDto = {
  id: emptyGuid,
  location: ButtonLocation.DataCapture,
  function: ButtonFunction.Submit,
  workflowService: '',
  workflowPlanId: 0,
  text: 'Submit',
  displayOrder: 1,
  downloadToTemplate: false,
  buttonMetadata: [],
};

const defaultConfiguration : ConfigurationDto = {
  id: emptyGuid,
  displayName: "",
  company: "",
  documentService: "",
  packageName: "",
  packageVersion: "",
  template: "",
  dialogDesign: "",
  customer: "",
  catalog: "",
  style: defaultStyle,
  icon: "",
  securityLevel: SecurityLevel.None,
  primaryColor: "",
  companyUrl: "",
  replaceIconsInCatalog: false,
  dataCaptureTemplateTitle: "Capture Data: {TemplateName}",
  dataCaptureTemplateSelectionTitle: "Select the Required Template",
  buttons: [defaultButton],
  simpleSecurityDefinition: {
    id:emptyGuid
  },
  enabled: true,
  entraSecurity: null
};

const ConfigurationEdit: React.FC = () => {
  
  const navigate = useNavigate();
  const flashCaptureWebsiteUrl = appConfig.FRONTEND_WEBSITE_BASE_URL + "/";
  const { configurationId } = useParams<{ configurationId: string }>();
  const { data, isFetching: isDataLoading } = useGetConfigurationQuery(configurationId!, {
    skip: !configurationId,
  });
  const [configuration, setConfiguration] = useState<ConfigurationDto>(
    configurationId ? defaultConfiguration : data || defaultConfiguration
  );
  const [form] = Form.useForm();
  const [isFormEdited, setIsFormEdited] = useState(false);
  useNavigationGuard(isFormEdited, "You have unsaved changes. Do you really want to leave this page?");
  const [isRequiredFieldsFilled, setIsRequiredFieldsFilled] = useState(false);

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(isDataLoading);
  }, [isDataLoading]);
  
  const [buttonList, setButtonList] = useState<ButtonItem[]>([]);
  const [dataCaptureButtons, setDataCaptureButtons] = useState<ButtonItem[]>([]);
  const [headerButtons, setHeaderButtons] = useState<ButtonItem[]>([]);
  const [selectedButton, setSelectedButton] = useState<ButtonItem>();
  const [uploadedFile, setUploadedFile] = useState<File | undefined>(undefined);

  const [fileList, setFileList] = useState<any[]>([]);

  const clearUploadedFile = () => {
    setUploadedFile(undefined);
    setFileList([]);
  };

  const [updateConfiguration] = useUpdateConfigurationMutation();

  const [configType, setConfigType] = useState<"Template" | "Catalog">(
    "Template"
  );
  const [selectedEntraId, setSelectedEntraId] = useState<EntraSecurityAdminDto | null> (null);
  
  const mapSecurityLevelToDisplay = (
    securityLevel: SecurityLevel
  ): SecurityDisplayLevel => {
    switch (securityLevel) {
      case SecurityLevel.None:
        return SecurityDisplayLevel.None;
      case SecurityLevel.Simple:
        return SecurityDisplayLevel.Simple;
      case SecurityLevel.Entra:
        return SecurityDisplayLevel.AzureAd;
      default:
        throw new Error(`Unknown SecurityDisplayLevel: ${securityLevel}`)
    }
  };

  const mapDisplayToSecurityLevel = (
    securityDisplayLevel: SecurityDisplayLevel
  ): SecurityLevel => {
    switch (securityDisplayLevel) {
      case SecurityDisplayLevel.None:
        return SecurityLevel.None;
      case SecurityDisplayLevel.Simple:
        return SecurityLevel.Simple;
      case SecurityDisplayLevel.AzureAd:
        return SecurityLevel.Entra;
      default:
        throw new Error(`Unknown SecurityDisplayLevel: ${securityDisplayLevel}`);
    }
  };

  const [isEntraModalVisible, setIsEntraModalVisible] = useState(false);
  const [isButtonEditModalVisible, setIsButtonEditModalVisible]  =
    useState(false);

  const filterButtonsByLocation = (
    buttonItems: ButtonItem[] | undefined,
    location: ButtonLocation
  ): ButtonItem[] => {
    return buttonItems?.filter((buttonItem) => buttonItem.button.location === location) || [];
  };

  const [styleData, setStyleData] = useState<StyleDto>(configuration.style);

  useEffect(() => {

    if (configurationId && data) {
      setConfiguration(data);
      form.setFieldsValue(data);
      setConfigType(data.template ? "Template" : "Catalog");
      setButtonList(data.buttons.map((button,index) => ({button, id: index+1})))
      setIsFormEdited(false);
      setSelectedEntraId(null);
      setIsRequiredFieldsFilled(true);
      if(data.securityLevel===SecurityLevel.Entra){
        setSelectedEntraId(data.entraSecurity);
      }
      setStyleData(data.style)
    } else {
      setConfiguration(defaultConfiguration);
      form.setFieldsValue(defaultConfiguration);
      setConfigType(defaultConfiguration.catalog ? "Catalog" : "Template");
      setButtonList(defaultConfiguration.buttons.map((button,index) => ({button, id: index+1})))
      setIsFormEdited(false);
      setIsRequiredFieldsFilled(false);
      setSelectedEntraId(null);
    } 
  }, [data, configurationId]);

  useEffect(() => {
    const filteredDataCaptureButtons = filterButtonsByLocation(
      buttonList,
      ButtonLocation.DataCapture
    );
    const filteredHeaderButtons = filterButtonsByLocation(
      buttonList,
      ButtonLocation.Header
    );
    setDataCaptureButtons(filteredDataCaptureButtons);
    setHeaderButtons(filteredHeaderButtons);
    if(configType === "Template"){
      form.validateFields(["catalog"])
    } else if (configType === "Catalog"){
      form.validateFields(["template","dialogDesign"])
    }
  }, [configuration, buttonList, configType, form]);
  useEffect(() => {
    let requiredFieldsFilled = !!configuration.displayName && !!configuration.documentService && !!configuration.packageName && !!configuration.packageVersion;
    if(configType === "Template"){
      requiredFieldsFilled = requiredFieldsFilled && !!configuration.template && !!configuration.dialogDesign;
    }
    setIsRequiredFieldsFilled(requiredFieldsFilled);
  }, [configType, configuration]);

  const handleInputChange = <K extends keyof ConfigurationDto>(
    field: K,
    value: ConfigurationDto[K]
  ) => {
    setIsFormEdited(true);
    setConfiguration((prev) =>
      prev ? { ...prev, [field]: value } : defaultConfiguration
    );
  };

  const handleStyleChange = <T extends keyof StyleDto>(
    key: T,
    value: string,
    setLocalState: (val: any) => void
  ) => {
    setLocalState(value);
    setStyleData((prev) => ({ ...prev, [key]: value }));
    setConfiguration((prev) =>
      prev ? { ...prev, style: { ...prev.style, [key]: value } } : prev
    );
    setIsFormEdited(true);
  };

  const handleNumericStyleChange = <T extends keyof StyleDto>(
    key: T,
    value: number | null,
    setLocalState: (val: number) => void
  ) => {
    if (value !== null) {
      const stringValue = value.toString();
      setLocalState(value);
      setStyleData((prev) => ({ ...prev, [key]: stringValue }));
      setConfiguration((prev) =>
        prev ? { ...prev, style: { ...prev.style, [key]: stringValue } } : prev
      );
      setIsFormEdited(true);
    }
  };

  const handleColorChange = <T extends keyof StyleDto>(
    key: T,
    color: { toHex: () => string },
    setLocalState: (val: string) => void
  ) => {
    const hex = color.toHex();
    setLocalState(hex);
    setStyleData((prev) => ({ ...prev, [key]: hex }));
    setConfiguration((prev) =>
      prev ? { ...prev, style: { ...prev.style, [key]: hex } } : prev
    );
    setIsFormEdited(true);
  };


  
  useEffect(() => {
    let isSecurityInvalid:boolean = configuration.securityLevel === SecurityLevel.Entra? selectedEntraId===null:false;
    setIsButtonDisabled(!isFormEdited || !isRequiredFieldsFilled || isSecurityInvalid);
  }, [isFormEdited, isRequiredFieldsFilled, configuration.securityLevel, selectedEntraId]);


  const isTemplate = configType === "Template";
  const isCatalog = configType === "Catalog";

  const onEditButtonClick = (button: ButtonItem) => {
    setSelectedButton(button);
    setIsButtonEditModalVisible(true);
  };
  const onDeleteButtonClick = (buttonItem: ButtonItem) => {
    setButtonList((prev) => prev.filter((button) => button.id !== buttonItem.id));
    setIsFormEdited(true);
  };

  const onNewButtonClick = (buttonLocation: ButtonLocation) => {
    let newButton: ButtonDto = {
      id: emptyGuid,
      location: buttonLocation,
      function: ButtonFunction.Other,
      workflowService: "",
      workflowPlanId: 0,
      text: "",
      displayOrder: -1,
      downloadToTemplate: false,
      buttonMetadata: [],
    };
    let newButtonItem: ButtonItem = {
      button: newButton,
      id: -1
    }
    setSelectedButton(newButtonItem);
    setIsButtonEditModalVisible(true);
  };

  const handleEntraCancel = () => {
    setIsEntraModalVisible(false);
  }

  const handleEntraSelect = (selectedEntry: EntraSecurityAdminDto) => {
    setIsFormEdited(true);
    setSelectedEntraId(selectedEntry);
  }

  const handleEntraButtonClick = () => {
    setIsEntraModalVisible(true);
  }

  const updateButtonListDisplayOrder = (updatedButtons: ButtonItem[]) => {
    const updatedAllButtons = buttonList.map((buttonItem) =>
      updatedButtons.find((updated) => updated.id === buttonItem.id) || buttonItem
    );
    setIsFormEdited(true);

    setButtonList(updatedAllButtons);
  };

  const handleButtonEditSave = (editedButtonItem: ButtonItem) => {
    setIsButtonEditModalVisible(false);
    setButtonList((prev) => {
      const updatedButtonItems = [...prev];
  
      if (editedButtonItem.id === -1) {
        const newId = updatedButtonItems.length > 0 
                ? Math.max(...updatedButtonItems.map(item => item.id)) + 1 
                : 0; 
        editedButtonItem.button.displayOrder = filterButtonsByLocation(buttonList, editedButtonItem.button.location).length+1
        const newButtonItem = { ...editedButtonItem, id: newId };
        updatedButtonItems.push(newButtonItem);
      } else {
        const indexToUpdate = updatedButtonItems.findIndex((item) => item.id === editedButtonItem.id);
        if (indexToUpdate !== -1) {
          updatedButtonItems[indexToUpdate] = editedButtonItem;
        } else {
          console.error(`Button with id ${editedButtonItem.id} not found.`);
        }
      }
  
      return updatedButtonItems;
    });
    setIsFormEdited(true);
  };
  const handleButtonEditCancel = () => {
    setIsButtonEditModalVisible(false);
  };
  const onEditUsersClick = () => {
    if(!isFormEdited && configuration.id!==emptyGuid){
      navigate(`/configuration/${configuration.id}/users`);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.readAsDataURL(file);
    });
  };
  
  const saveConfiguration = async () => {
    let newButtonList: ButtonDto[] = buttonList.map((buttonItem) => buttonItem.button);
    let newConfiguration: ConfigurationDto = {
      ...configuration, buttons: newButtonList
    }
    if(newConfiguration.style.primaryColor !== undefined){
      newConfiguration.primaryColor = "#" + newConfiguration.style.primaryColor;
    }
    if(configType === "Catalog"){
      newConfiguration.template = "";
    }
    else if(configType === "Template"){
      newConfiguration.catalog = "";
    }
    if(uploadedFile !== undefined){
      const base64Icon = await fileToBase64(uploadedFile);
      newConfiguration.icon = base64Icon;
    }
    if(newConfiguration.securityLevel===SecurityLevel.Entra && selectedEntraId!== null){
      newConfiguration.entraSecurity = selectedEntraId;
    }
    setIsLoading(true);
    let updatedConfig = (await updateConfiguration({configuration: newConfiguration})).data;
    setIsLoading(false);
    setIsFormEdited(false);
    if(updatedConfig!==undefined){
      setConfiguration(updatedConfig);
    }
    setFileList([]); 
    setUploadedFile(undefined);
  }
  

  
  const [primaryColor, setPrimaryColor] = useState(styleData.primaryColor);
  const [secondaryColor, setSecondaryColor] = useState(styleData.secondaryColor);
  const [backgroundColor, setBackgroundColor] = useState(styleData.backgroundColor);
  const [titleDisplay, setTitleDisplay] = useState(styleData.titleDisplay === 'flex');
  const [titleSize, setTitleSize] = useState(Number(styleData.titleSize));
  const [subTitleSize, setSubTitleSize] = useState(Number(styleData.subTitleSize));
  const [titleFont, setTitleFont] = useState(styleData.titleFont);
  const [subTitleFont, setSubTitleFont] = useState(styleData.subTitleFont);
  const [logoDisplay, setLogoDisplay] = useState(styleData.logoDisplay === 'flex');
  const [logoSize, setLogoSize] = useState(Number(styleData.logoSize));
  const [logoOrder, setLogoOrder] = useState(Number(styleData.logoOrder) === 0 || Number(styleData.logoOrder) === 2 ? Number(styleData.logoOrder) : 0);
  const [loginLogoSize, setLoginLogoSize] = useState(Number(styleData.loginPageLogoSize));

  useEffect(() => {
    if (!styleData) return;

    setPrimaryColor(styleData.primaryColor);
    setSecondaryColor(styleData.secondaryColor);
    setBackgroundColor(styleData.backgroundColor);
    setTitleDisplay(styleData.titleDisplay === 'flex');
    setTitleSize(Number(styleData.titleSize));
    setSubTitleSize(Number(styleData.subTitleSize));
    setTitleFont(styleData.titleFont);
    setSubTitleFont(styleData.subTitleFont);
    setLogoDisplay(styleData.logoDisplay === 'flex');
    setLogoSize(Number(styleData.logoSize));
    setLogoOrder(
      Number(styleData.logoOrder) === 0 || Number(styleData.logoOrder) === 2
        ? Number(styleData.logoOrder)
        : 0
    );
    setLoginLogoSize(Number(styleData.loginPageLogoSize));
  }, [styleData]);

  return (
    <>
    {isLoading ? (<CustomSpinner />) : (
    <Form 
    form={form} 
    layout="vertical" 
    className="configuration-edit-form"
    onValuesChange={(changedValues) => {
      const [field, value] = Object.entries(changedValues)[0];
      handleInputChange(field as keyof ConfigurationDto, value as ConfigurationDto[keyof ConfigurationDto]);
    }}
    >
      <Title level={2}>Settings</Title>
      <Title level={3} className="level-3-title">Configuration Details</Title>

      <Row>
        <Col span={4}>
          <label htmlFor="displayName">Name<span className="required-indicator"> *</span></label>
        </Col>
        <Col span={6}>
          <Form.Item
            name="displayName"
            validateTrigger="onBlur"
            rules={[
              { required: true, message: "Required" },
            ]}
          >
            <Input
              data-testid="nameInput"
            />
          </Form.Item>
        </Col>
      </Row>
      
      {(configuration.id!=="" && configuration.id!==emptyGuid) && 
      <Row>
        <Col span={4}>
          <label htmlFor="url">Url</label>
        </Col>
        <Col span={16}>
          <a 
            href={flashCaptureWebsiteUrl + configuration.company + "/" + configurationId!}
            target="_blank" 
            rel="noopener noreferrer"
            className={configuration.enabled ? "link-enabled" : "link-disabled"}
          >
            {flashCaptureWebsiteUrl + configuration.company + "/" + configurationId!}
          </a>
          <Button 
            icon={<CopyOutlined />} 
            size="small"
            data-testid="copyUrlButton"
            disabled={!configuration.enabled}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(flashCaptureWebsiteUrl + configuration.company + "/" + configurationId!);
                message.success("URL copied to clipboard");
              } catch (err) {
                message.error("Failed to copy URL");
              }
            }}
          />
        </Col>
      </Row>}

      <Row>
        <Col span={4}>
          <label className="checkbox-label" htmlFor="enabled">Enabled<span className="required-indicator"> *</span></label>
        </Col>
        <Col span={6}>
          <Form.Item>
              <Checkbox
                checked={configuration.enabled}
                onChange={(e) => handleInputChange("enabled", e.target.checked)}
              data-testid="enabledInput"
                />
          </Form.Item>
        </Col>
      </Row>

      <Title level={3} className="level-3-title">Authentication</Title>
      <Row>
        <Col span={4}>
          <label htmlFor="securityLevel">Type<span className="required-indicator"> *</span></label>
        </Col>
        <Col span={14}>
          <Form.Item>
            <Radio.Group
              value={mapSecurityLevelToDisplay(configuration.securityLevel)} 
              onChange={(e) => {
                const selectedDisplay = e.target.value;
                const enumValue = mapDisplayToSecurityLevel(selectedDisplay);
                handleInputChange("securityLevel", enumValue);
              }}
              data-testid="securityLevelInput"
            >
              {Object.values(SecurityDisplayLevel).map((level) => (
                <Radio key={level} value={level}>
                  {level} 
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      {(configuration.securityLevel===SecurityLevel.Simple && configuration.id !== emptyGuid && !isFormEdited) &&
        <Row>
        <Col span={4}>
          <label htmlFor="listedUsersInfo">Listed Users info</label>
        </Col>
        <Col span={6}>
          <Button icon={<UserOutlined />} className="access-control-users-button" onClick={onEditUsersClick}>Manage Users</Button>
        </Col>
      </Row>
      }
      {(configuration.securityLevel===SecurityLevel.Simple && (configuration.id === emptyGuid || isFormEdited)) &&
      <Row>
        <Col span={4}>
          <label htmlFor="listedUsersInfo">Listed Users info</label>
        </Col>
        <Col span={6}>
          <Button icon={<UserOutlined />} className="access-control-users-button" disabled={true}>Manage Users</Button>
          <div className="configuration-edit-form users-message">Save the configuration before managing users</div>
        </Col>
      </Row>
      }
      {(configuration.securityLevel===SecurityLevel.Entra) && 
      <Row>
        <Col span={4}>
          <label htmlFor="entraInfo">Entra info</label>
        </Col>
        <Col span={6}>
          <Button icon={<GlobalOutlined />} className="entra-modal-button" onClick={handleEntraButtonClick}>Manage Entra Group</Button>
          {(configuration.securityLevel===SecurityLevel.Entra && selectedEntraId===null) && 
          <div className="configuration-edit-form entra-warning">Please select an entra group</div>}
        </Col>
      </Row>
      }

      <Title  level={3} className="level-3-title">Project</Title>
      <Row>
        <Col span={4}>
          <label htmlFor="documentService">Document Service<span className="required-indicator"> *</span></label>
        </Col>
        <Col span={6}>
          <Form.Item
            name="documentService"
            validateTrigger="onBlur"
            rules={[
              { required: true, message: "Required" },
            ]}
            >
            <Input
              data-testid="documentServiceInput"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={4}>
          <label htmlFor="packageName">Package Name<span className="required-indicator"> *</span></label>
        </Col>
        <Col span={6}>
          <Form.Item
            name="packageName"
            validateTrigger="onBlur"
            rules={[
              { required: true, message: "Required" },
            ]}
            >
            <Input
              data-testid="packageNameInput"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <label htmlFor="packageVersion">Package Version<span className="required-indicator"> *</span></label>
        </Col>
        <Col span={6}>
          <Form.Item
            name="packageVersion"
            validateTrigger="onBlur"
            rules={[
              { required: true, message: "Required" },
            ]}
            >
            <Input
              data-testid="packageVersionInput"
            />
          </Form.Item>
        </Col>
      </Row>

      <Title level={3} className="level-3-title">Template & Dialog</Title>
      <Row>
        <Col span={4}>
          <label htmlFor="configType">Type<span className="required-indicator"> *</span></label>
        </Col>
        <Col span={6}>
          <Form.Item>
            <Radio.Group
              value={configType}
              onChange={(e) => setConfigType(e.target.value)}
              data-testid="catalogTypeInput"
            >
              <Radio value="Template">Single Template</Radio>
              <Radio value="Catalog">Catalog</Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={4}>
          <label htmlFor="catalog">Catalog Name</label>
        </Col>
        <Col span={6}>
          <Form.Item>
            <Input
              value={configuration?.catalog}
              onChange={(e) =>
                handleInputChange(
                  "catalog",
                  e.target.value
                )
              }
              disabled={isTemplate}
              data-testid="catalogInput"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <label htmlFor="dataCaptureTemplateSelectionTitle">Template Selection Title</label>
        </Col>
        <Col span={6}>
          <Form.Item>
            <Input
              value={configuration?.dataCaptureTemplateSelectionTitle}
              onChange={(e) =>
                handleInputChange(
                  "dataCaptureTemplateSelectionTitle",
                  e.target.value
                )
              }
              disabled={isTemplate}
              data-testid="templateSelectionTitleInput"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <label htmlFor="template">Template Name{isTemplate && <span className="required-indicator"> *</span>}</label>
        </Col>
        <Col span={6}>
          <Form.Item
            name="template"
            validateTrigger="onBlur"
            rules={[
              { required: isTemplate, message: "Required" },
            ]}
            >
            <Input
              disabled={isCatalog}
              data-testid="templateInput"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={4}>
          <label htmlFor="dialogDesign">Dialog Design Name{isTemplate && <span className="required-indicator"> *</span>}</label>
        </Col>
        <Col span={6}>
          <Form.Item
            name="dialogDesign"
            validateTrigger="onBlur"
            rules={[
              { required: isTemplate, message: "Required" },
            ]}
            >
            <Input
              disabled={isCatalog}
              data-testid="dialogDesignInput"
            />
          </Form.Item>
        </Col>
      </Row>

      <Title level={3} className="level-3-title">Title Bar</Title>
      <Row>
        <Col span={4}>
          <label htmlFor="customer">Main Title</label>
        </Col>
        <Col span={6}>
          <Form.Item>
            <Input
              value={configuration?.customer}
              onChange={(e) =>
                handleInputChange("customer", e.target.value)
              }
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <label htmlFor="showTitle">Show Title</label>
        </Col>
        <Col span={1}>
          <Checkbox
            data-testid="showTitleCheckbox"
            checked={titleDisplay}
            onChange={(e) => { handleStyleChange("titleDisplay", e.target.checked ? "flex" : "none", () => setTitleDisplay(e.target.checked));}}
          />
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <label htmlFor="dataCaptureTemplateTitle">Subtitle</label>
        </Col>
        <Col span={6}>
          <Form.Item>
            <Input
              value={configuration?.dataCaptureTemplateTitle}
              onChange={(e) =>
                handleInputChange("dataCaptureTemplateTitle", e.target.value)
              }
              data-testid="dialogDisplayNameInput"
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <label htmlFor="logoUpload">Upload Logo File</label>
        </Col>
        <Col span={8}>
          <div className="upload-controls">
            <Upload
              accept=".png"
              beforeUpload={(file) => {
                setUploadedFile(file);
                setFileList([file]);
                setIsFormEdited(true);
                return false;
              }}
              fileList={fileList}
              onRemove={clearUploadedFile}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
            <Button onClick={clearUploadedFile}>
              Reset
            </Button>
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <label htmlFor="showLogo">Show Logo</label>
        </Col>
        <Col span={6}>
          <Checkbox
            checked={logoDisplay}
            onChange={(e) => { handleStyleChange("logoDisplay", e.target.checked ? "flex" : "none", () => setLogoDisplay(e.target.checked));}}
          />
        </Col>
      </Row>
      
      <Row>
        <Col span={4}>
          <label htmlFor="logoOrder">Logo Order</label>
        </Col>
        <Col span={6}>
          <Radio.Group
            disabled={!(logoDisplay && titleDisplay)}
            value={logoOrder}
            onChange={(e) => handleNumericStyleChange("logoOrder", e.target.value, () => setLogoOrder(e.target.value))}
          >
            <Radio value={0}>Logo/Title</Radio>
            <Radio value={2}>Title/Logo</Radio>
          </Radio.Group>
        </Col>
      </Row>
      
      <Title level={3} className="level-3-title">Styling</Title>
      <Row>
        <Col span={4}>
          <label htmlFor="primaryColor">Primary Color</label>
        </Col>
        <Col span={10}>
          <Space>
            <ColorPicker value={primaryColor} onChange={(color) => handleColorChange('primaryColor', color, setPrimaryColor)} data-testid="primaryColorInput" data-color={primaryColor}/>
            <Button data-testid="primaryColorReset" size="small" onClick={() => handleStyleChange('primaryColor', defaultStyle.primaryColor!, setPrimaryColor)}>Reset</Button>
            <text className="textColorDescription">Subtitle, section headers, line breaks, button text</text>
          </Space>
        </Col>
      </Row>
      <Row>
        <Col span={4}>
          <label htmlFor="secondaryColor">Secondary Color</label>
        </Col>
        <Col span={6}>
          <Space direction="horizontal" size="small">
            <ColorPicker value={secondaryColor} onChange={(color) => handleColorChange('secondaryColor', color, setSecondaryColor)} data-testid="secondaryColorInput" data-color={secondaryColor}/>
            <Button data-testid="secondaryColorReset" size="small" onClick={() => handleStyleChange('secondaryColor', defaultStyle.secondaryColor!, setSecondaryColor)}>Reset</Button>
            <text className="textColorDescription">Main title, button background</text>
          </Space>
        </Col>
      </Row>

      <Row>
        <Col span={4}>
          <label htmlFor="backgroundColor">Title Bar Color</label>
        </Col>
        <Col span={6}>
          <Space direction="horizontal" size="small">
            <ColorPicker value={backgroundColor} onChange={(color) => handleColorChange('backgroundColor', color, setBackgroundColor)} data-testid="backgroundColorInput" data-color={backgroundColor}/>
            <Button data-testid="backgroundColorReset" size="small" onClick={() => handleStyleChange('backgroundColor', defaultStyle.backgroundColor!, setBackgroundColor)}>Reset</Button>
          </Space>
        </Col>
      </Row>

      <Row>
        <Col span={4}>
          <label htmlFor="titleSize">Main Title Size</label>
        </Col>
        <Col span={6}>
          <InputNumber
            min={10}
            max={50}
            value={titleSize}
            onChange={(value) =>  handleNumericStyleChange("titleSize", value, setTitleSize)}
          />
        </Col>
      </Row>

      <Row>
        <Col span={4}>
          <label htmlFor="titleFont">Main Title Font</label>
        </Col>
        <Col span={6}>
          <Select
            className="font-select"
            data-testid="titleFontInput"
            value={titleFont}
            onChange={(value) => handleStyleChange("titleFont", value, setTitleFont)}
          >
            <Option value="Arial">Arial</Option>
            <Option value="Verdana">Verdana</Option>
            <Option value="Roboto">Roboto</Option>
            <Option value="Tahoma">Tahoma</Option>
          </Select>
        </Col>
      </Row>

      <Row>
        <Col span={4}>
          <label htmlFor="subtitleSize">Subtitle Size</label>
        </Col>
        <Col span={6}>
          <InputNumber
            min={10}
            max={50}
            value={subTitleSize}
            onChange={(value) => handleNumericStyleChange("subTitleSize", value, setSubTitleSize)}
          />
        </Col>
      </Row>

      <Row>
        <Col span={4}>
          <label htmlFor="subtitleFont">Subtitle Font</label>
        </Col>
        <Col span={6}>
          <Select
            className="font-select"
            value={subTitleFont}
            onChange={(value) => handleStyleChange("subTitleFont", value, setSubTitleFont)}
          >
            <Option value="Arial">Arial</Option>
            <Option value="Verdana">Verdana</Option>
            <Option value="Roboto">Roboto</Option>
            <Option value="Tahoma">Tahoma</Option>
          </Select>
        </Col>
      </Row>

      <Row>
        <Col span={4}>
          <label htmlFor="logoSize">Logo Size %</label>
        </Col>
        <Col span={6}>
          <InputNumber
            data-testid="logoSizeInput"
            min={50}
            max={150}
            value={logoSize}
            onChange={(value) => handleNumericStyleChange("logoSize", value, setLogoSize)}
          />
        </Col>
      </Row>

      <Row>
        <Col span={4}>
          <label htmlFor="loginLogoSize">Login Page Logo Size %</label>
        </Col>
        <Col span={6}>
          <InputNumber
            min={50}
            max={150}
            value={loginLogoSize}
            onChange={(value) => handleNumericStyleChange("loginPageLogoSize", value, setLoginLogoSize)}
          />
        </Col>
      </Row>


      <Title level={3} className="level-3-title">Title Bar Buttons</Title>
      <Row className="button-list-row">
        <Col>
          <SortableButtonList
            editButton={onEditButtonClick}
            newButton={onNewButtonClick}
            deleteButton={onDeleteButtonClick}
            buttonList={headerButtons}
            buttonLocation={ButtonLocation.Header}
            updateButtonListDisplayOrder={updateButtonListDisplayOrder}
          />
        </Col>
      </Row>

      <Title level={3} className="level-3-title">Data Capture Buttons</Title>
      <Row className="button-list-row">
        <Col>
          <SortableButtonList
            editButton={onEditButtonClick}
            newButton={onNewButtonClick}
            deleteButton={onDeleteButtonClick}
            buttonList={dataCaptureButtons}
            buttonLocation={ButtonLocation.DataCapture}
            updateButtonListDisplayOrder={updateButtonListDisplayOrder}
          />
        </Col>
      </Row>
      
      <Button onClick={() => navigate("/")} className="cancel-button" size="large">
        Cancel
      </Button>

      <Button type="primary" htmlType="submit" onClick={saveConfiguration} className="save-button" size="large" disabled={isButtonDisabled}>
        Save
      </Button>
      
      {isEntraModalVisible && <EntraSecurityModal visible={isEntraModalVisible} selectedEntraSecurity={selectedEntraId} onClose={handleEntraCancel} onSelect={handleEntraSelect}/>}
      {isButtonEditModalVisible && selectedButton && (
        <ButtonEditModal
          isVisible={isButtonEditModalVisible}
          buttonItem={selectedButton}
          onSave={handleButtonEditSave}
          onClose={handleButtonEditCancel}
        />
      )}
    </Form>)}
    </>
  );
};

export default ConfigurationEdit;
